import { useReducer, useEffect, useRef } from 'react';

import loadScript from '../load-script';

enum EActionType {
  SetPending = 'SET_IS_PENDING',
  SetReady = 'SET_READY',
  SetError = 'SET_ERROR',
}

interface IState {
  isPending: boolean;
  didError: boolean;
}

type TAction =
  | { type: EActionType.SetPending }
  | { type: EActionType.SetReady }
  | { type: EActionType.SetError };

function reducer(state: IState, action: TAction): IState {
  switch (action.type) {
    case EActionType.SetPending:
      return {
        ...state,
        isPending: true,
        didError: false,
      };
    case EActionType.SetReady:
      return {
        ...state,
        isPending: false,
        didError: false,
      };
    case EActionType.SetError:
      return {
        ...state,
        isPending: false,
        didError: true,
      };
    default:
      return state;
  }
}

export default function useScript({
  url,
  async,
}: {
  url: string;
  async?: boolean;
}): {
  isPending: boolean;
  didError: boolean;
  isReady: boolean;
} {
  const asyncRef = useRef(async);
  const [{ isPending, didError }, dispatch] = useReducer(reducer, {
    isPending: true,
    didError: false,
  });

  useEffect(() => {
    asyncRef.current = async;
  });

  useEffect(
    (): void => {
      dispatch({ type: EActionType.SetPending });

      loadScript({ url, async: asyncRef.current })
        .then(() => {
          dispatch({ type: EActionType.SetReady });
        })
        .catch(() => {
          dispatch({ type: EActionType.SetError });
        });
    },
    [url]
  );

  return {
    isPending,
    didError,
    isReady: !isPending && !didError,
  };
}
